import { FC, useEffect, useState } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { Input } from '../../../components/input/index';
import { CompanyAvatar } from './company-avatar';
import { PublicStatusCard } from './public-status-card';

type PasswordGateProps = {
  companyLogoUrl: null | string;
  companyName: null | string;
  hasFailed: boolean;
  isSubmitting: boolean;
  onSubmit: (password: string) => void;
  throttledForSeconds: null | number;
};

export const PasswordGate: FC<PasswordGateProps> = ({
  companyLogoUrl,
  companyName,
  hasFailed,
  isSubmitting,
  onSubmit,
  throttledForSeconds,
}) => {
  const [password, setPassword] = useState('');
  // Counted down locally so the form unlocks on its own; without it the gate stayed disabled until
  // the viewer thought to reload the page.
  const [secondsLeft, setSecondsLeft] = useState(throttledForSeconds);

  useEffect(() => setSecondsLeft(throttledForSeconds), [throttledForSeconds]);

  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) {
      return;
    }

    const timer = setTimeout(
      () => setSecondsLeft((left) => (left === null ? null : left - 1)),
      1000,
    );

    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const isThrottled = secondsLeft !== null && secondsLeft > 0;

  return (
    <PublicStatusCard>
      <CompanyAvatar
        className="text-2xl size-14"
        companyName={companyName}
        logoUrl={companyLogoUrl}
      />

      <span className="text-center text-sm font-[450] leading-5 text-gray-500">
        <AppFormattedMessage
          id="SHARE_PUBLIC_PASSWORD_PROMPT"
          values={{ company: companyName ?? '' }}
        />
      </span>

      <form
        className="flex w-full flex-col gap-10"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(password);
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Input
            disabled={isThrottled}
            onChange={setPassword}
            placeholder={<AppFormattedMessage id="PASSWORD" />}
            type="password"
            value={password}
            wrapperClassName="w-full"
          />

          {isThrottled ? (
            <span className="text-xs font-[450] text-fireside-600">
              <AppFormattedMessage
                id="SHARE_PUBLIC_THROTTLED"
                values={{ minutes: Math.ceil((secondsLeft ?? 0) / 60) }}
              />
            </span>
          ) : (
            hasFailed && (
              <span className="text-xs font-[450] text-fireside-600">
                <AppFormattedMessage id="SHARE_PUBLIC_WRONG_PASSWORD" />
              </span>
            )
          )}
        </div>

        <Button
          className="h-11 w-full rounded text-sm font-[550] text-white"
          disabled={!password || isSubmitting || isThrottled}
          isLoading={isSubmitting}
          type="submit"
        >
          <AppFormattedMessage id="SHARE_PUBLIC_OPEN" />
        </Button>
      </form>
    </PublicStatusCard>
  );
};
